import { FC, useEffect, useState } from "react"
import { Supplier } from "../../../../types/slices/supplierType"
import { Box, Divider, Drawer, Grid, IconButton, Stack, TableCell, TableRow, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import CustomTable from "../../../../components/common/CustomTable/CustomTable"
import { useAccountReceivableStore } from "../../../../hooks/useAccountReceivableStore"
import { formatDate, formatDateTime } from "../../../../toolbox/helpers/formatDateHelper"
import { moneyCL } from "../../../../toolbox/helpers/money-helpers"
import { ApiStatus } from "../../../../types/api/status"
import { CustomSnackbar } from "../../../../components/common/CustomSnackbar/CustomSnackbar"
import { PayReceivableDrawer } from "./PayReceivableDrawer"

interface Props {
    open: boolean
    onClose: () => void
    customer: Supplier
    onList: () => Promise<void>
}

interface onList {
    page?: number
    perPage?: number
    idsupplier?: number
    search?: string
}

const columns = [
    // { type: 'checkbox', field: 'checkbox', label: 'checkbox', align: 'center' },
    { type: 'text', field: 'folio', label: 'N° Folio', align: 'center' },
    { type: 'text', field: 'type_document', label: 'Tipo documento', align: 'center' },
    { type: 'text', field: 'way_to_pay', label: 'Forma de pago', align: 'center' },
    { type: 'text', field: 'ticket_date', label: 'Emisión', align: 'center', format: (row) => formatDateTime(row.ticket_date) },
    { type: 'text', field: 'deadline', label: 'Vencimiento', align: 'right', format: (row) => formatDate(row.deadline) },
    { type: 'text', field: 'total', label: 'Total factura', align: 'right', format: (row) => moneyCL(row.total) },
    { type: 'text', field: 'amount_paid', label: 'Monto pagado', align: 'right', format: (row) => moneyCL(row.amount_paid) },
    { type: 'text', field: 'debt', label: 'Saldo pendiente', align: 'right', format: (row) => moneyCL(row.debt) },
]

export const AccountReceivableDrawer: FC<Props> = (props) => {
    const { open, onClose, customer, onList } = props

    const { status: statusAccounts, accounts, totalRows, getAccountsReceivable,
        getAccountExcel, getAccountPDF
    } = useAccountReceivableStore()

    const [selectedRows, setSelectedRows] = useState<any[]>([])
    const [allSelected, setAllSelected] = useState<boolean>(false)
    const [payView, setPayView] = useState<boolean>(false)

    const [search, setSearch] = useState<string>('')
    const [page, setPage] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(25)

    const [totalInvoce, setTotalInvoce] = useState<number>(0)
    const [totalPayment, setTotalPayment] = useState<number>(0)
    const [totalDebt, setTotalDebt] = useState<number>(0)

    useEffect(() => {
        if (search === '') {
            getDataAPI()
        } else {
            const timer = setTimeout(() => getDataAPI(), 500)
            return () => clearTimeout(timer)
        }
    }, [search, page, perPage])

    useEffect(() => {
        if (accounts.length > 0) {
            const totalInvoice = accounts.reduce((acc, row) => acc + row.total, 0)
            const totalPayment = accounts.reduce((acc, row) => acc + row.amount_paid, 0)
            const totalDebt = accounts.reduce((acc, row) => acc + row.debt, 0)

            setTotalInvoce(totalInvoice)
            setTotalPayment(totalPayment)
            setTotalDebt(totalDebt)
        }
    }, [accounts])

    const getDataAPI = async () => {
        const payload: any = {
            idsupplier: customer.id,
            paid: "0",
            orderBy: 'deadline',
            order: 'asc',
            page: page,
            perPage: perPage,
            withRowColor: true
        }
        if (search !== '') payload.search = search
        await getAccountsReceivable(payload)
    }

    const handleChangePage = (event: any, newPage: any) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (event) => {
        setPerPage(+event.target.value)
        setPage(1)
    }


    const onSearch = (search) => setSearch(search)

    const isSelected = (row: any) => selectedRows.some(selectedRow => selectedRow.id === row.id)

    const onCheckbox = (row: any) => {
        const selectedIndex = selectedRows.findIndex(selectedRow => selectedRow.id === row.id)
        let newSelected: any = []

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedRows, row)
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedRows.slice(1))
        } else if (selectedIndex === selectedRows.length - 1) {
            newSelected = newSelected.concat(selectedRows.slice(0, -1))
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedRows.slice(0, selectedIndex),
                selectedRows.slice(selectedIndex + 1)
            )
        }
        setSelectedRows(newSelected)
        setAllSelected(newSelected.length === accounts.length)

    }

    const selectAllRows = () => {
        if (allSelected) {
            setSelectedRows([])
            setAllSelected(false)
        } else {
            setSelectedRows(accounts)
            setAllSelected(true)
        }
    }

    const onPay = () => {
        if (selectedRows.length === 0) return CustomSnackbar('error', 'Seleccione un comprobante.')
        setPayView(true)
    }

    const finishRow = () => {
        return (
            <TableRow>
                <TableCell colSpan={5} sx={{ backgroundColor: '#69ABC3', color: '#fff', textAlign: 'center' }}>
                    Total
                </TableCell>
                <TableCell sx={{ textAlign: 'right' }}>
                    {moneyCL(totalInvoce)}
                </TableCell>
                <TableCell sx={{ color: 'green', textAlign: 'right' }}>
                    {moneyCL(totalPayment)}
                </TableCell>
                <TableCell sx={{ color: 'red', textAlign: 'right' }}>
                    {moneyCL(totalDebt)}
                </TableCell>
            </TableRow>
        )
    }

    const onExport = async () => {
        const resp = await getAccountExcel(customer?.id)
        if (resp !== '') {
            window.open(resp, '_blank')
        }
    }

    const onDownloadPDF = async () => {
        const resp = await getAccountPDF(customer?.id)
        if (resp !== '') {
            window.open(resp, '_blank')
        }
    }

    return (
        <Drawer
            open={open}
            onClose={onClose}
            anchor="right"
        >
            <Grid container sx={{ width: 950, padding: 2 }} spacing={2}>
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        top: 8,
                        right: 8
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Grid item xs={12}>
                    <Typography variant="h6">
                        Cuentas por cobrar
                    </Typography>
                </Grid>
                <Grid item xs={12} mb={1}>
                    <Divider />
                </Grid>
                <Grid item xs={12}
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        marginBottom: '10px'
                    }}>
                    <Typography variant='h6' component='h1' gutterBottom sx={{ mt: 1, ml: 1 }} className='title-main'>
                        {customer?.name_rz + ' - ' + customer?.rut}
                    </Typography>
                </Grid>
                <Grid item xs={4}>
                    <Box
                        sx={{
                            backgroundColor: "#f5f5f5",
                            borderRadius: 2,
                            padding: 2,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight="bold">Facturas no vencidas</Typography>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="subtitle1" fontWeight="bold">N° Facturas:</Typography>
                            <Typography variant="subtitle1">{customer?.accounts_receivable_not_expired}</Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="subtitle1" fontWeight="bold">Monto Total:</Typography>
                            <Typography variant="subtitle1">{moneyCL(customer?.amount_receivable_not_expired)}</Typography>
                        </Stack>
                    </Box>
                </Grid>
                <Grid item xs={4}>
                    <Box
                        sx={{
                            backgroundColor: "#f5f5f5",
                            borderRadius: 2,
                            padding: 2,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight="bold">Factuas vencidas</Typography>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="subtitle1" fontWeight="bold">N° Facturas:</Typography>
                            <Typography variant="subtitle1" color="error">{customer?.accounts_receivable_expired}</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="subtitle1" fontWeight="bold">Monto total:</Typography>
                            <Typography variant="subtitle1" color="error">{moneyCL(customer?.amount_receivable_expired)}</Typography>
                        </Stack>

                    </Box>
                </Grid>
                <Grid item xs={4}>
                    <Box
                        sx={{
                            backgroundColor: "#f5f5f5",
                            borderRadius: 2,
                            padding: 2,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight="bold">Totales</Typography>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="subtitle1" fontWeight="bold">N° Facturas:</Typography>
                            <Typography variant="subtitle1">{customer?.accounts_receivable}</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="subtitle1" fontWeight="bold">Monto total:</Typography>
                            <Typography variant="subtitle1">{moneyCL(customer?.amount_receivable)}</Typography>
                        </Stack>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <CustomTable
                        columns={columns}
                        rows={accounts || []}
                        hasOptions
                        onSearch={onSearch}
                        // onAddFn={onPay}
                        // onAddFnName={'Pagar'}
                        loading={statusAccounts === ApiStatus.FETCHING}
                        isSelected={isSelected}
                        onCheckbox={onCheckbox}
                        selectAll={selectAllRows}
                        allSelected={allSelected}
                        dense
                        finishRow={finishRow}
                        onDownloadExcel={onExport}
                        onDownloadPDF={onDownloadPDF}

                        totalRows={totalRows}
                        rowsPerPage={perPage}
                        page={page}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        withCustomPagination
                    />
                </Grid>
                {payView && (
                    <PayReceivableDrawer
                        open={payView}
                        onClose={() => setPayView(false)}
                        selecteds={selectedRows}
                        onList1={onList}
                        onList2={getDataAPI}
                    />
                )}
            </Grid>
        </Drawer>
    )
}